import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { ArrowRight, School, Users, Shield, BookOpen } from 'lucide-react';
import { isApprovedMentorParent } from '@/lib/landing';

export default function HomePage() {
  const { user } = useAuth();

  const features = [
    {
      icon: <School className="h-6 w-6 text-[#F97D6A]" />,
      title: 'School Ecosystems',
      desc: 'Each school operates as a secure, closed environment for career discovery.',
    },
    {
      icon: <Users className="h-6 w-6 text-[#F97D6A]" />,
      title: 'Parent & Mentor Network',
      desc: 'Parents connect as mentors, sharing professional experiences with students.',
    },
    {
      icon: <Shield className="h-6 w-6 text-[#F97D6A]" />,
      title: 'Safe & Verified',
      desc: 'Invite-only access ensures only verified parents and coordinators participate.',
    },
    {
      icon: <BookOpen className="h-6 w-6 text-[#F97D6A]" />,
      title: 'Career Discovery',
      desc: 'Students explore real career paths through guided sessions and mentoring.',
    },
  ];

  // An approved mentor-parent returning to the site opens in the mentor workspace
  if (isApprovedMentorParent(user)) return <Navigate to="/mentor" replace />;

  return (
    <div data-testid="home-page">
      {/* Hero */}      <section className="hero-pattern py-12 sm:py-16 lg:py-20" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-14 items-center">
            <div className="animate-fade-in-up">
              <span className="inline-block text-[11px] font-bold uppercase tracking-[0.2em] text-[#F97D6A] bg-[#FFF0ED] px-3 py-1.5 rounded-full mb-6">
                School Edition
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-[3.5rem] font-extrabold tracking-tight text-[#1F2937] font-['Outfit'] leading-[1.08]">
                You&apos;ve read about the career.{' '}
                <br className="hidden sm:block" />
                <span className="text-[#F97D6A]">Now get the POV.</span>
              </h1>

              <p className="mt-6 text-[15px] sm:text-base text-[#6B7280] max-w-lg leading-relaxed">
                Explore careers. Meet professionals. Experience real workplaces. Discover what a career is actually like before choosing your path.
              </p>

              <div className="flex flex-wrap gap-3 mt-8">
                <Link to="/careers">
                  <Button
                    className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl text-sm font-semibold px-6 py-3 h-auto"
                    data-testid="hero-explore-btn"
                  >
                    Explore Careers
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button
                    variant="outline"
                    className="rounded-xl text-sm font-semibold px-6 py-3 h-auto border-gray-200 text-[#1F2937] hover:bg-gray-50"
                    data-testid="hero-demo-btn"
                  >
                    Request Demo
                  </Button>
                </Link>
              </div>

              <p className="mt-8 text-xs sm:text-sm font-bold uppercase tracking-[0.22em] text-[#F97D6A]"
                 data-testid="hero-tagline">
                From curiosity to clarity
              </p>
            </div>

            <div className="animate-fade-in-up stagger-2">
              <div className="relative">
                <div className="absolute -inset-3 bg-[#F97D6A]/5 rounded-3xl" />
                <picture>
                  <source
                    type="image/webp"
                    srcSet="/api/images/hero-main-800.webp 800w, /api/images/hero-main.webp 1400w"
                    sizes="(max-width: 1024px) 100vw, 600px"
                  />
                  <img
                    src="/api/images/hero-main.jpg"
                    alt="Parent and child exploring career paths together"
                    width="1400"
                    height="933"
                    loading="eager"
                    fetchpriority="high"
                    decoding="async"
                    className="relative rounded-2xl w-full object-cover h-[260px] sm:h-[300px] lg:h-[380px]"
                  />
                </picture>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Origin Story */}
      <section className="py-12 sm:py-16" data-testid="origin-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-[#9CA3AF] font-medium tracking-wider mb-3"><span className="uppercase">CareersPOV</span> started with a simple question:</p>
          <p className="text-xl sm:text-2xl font-medium text-[#1F2937] font-['Outfit'] italic leading-snug">
            &ldquo;Can I experience careers before I choose my subjects?&rdquo;
          </p>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 sm:py-20" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#1F2937] font-['Outfit']">
              How It Works
            </h2>
            <p className="mt-3 text-base sm:text-lg text-[#4B5563] max-w-2xl mx-auto">
              A trusted, school-based platform where students connect with real professionals to explore careers beyond information.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f, i) => (
              <div
                key={f.title}
                className={`bg-white rounded-2xl border border-gray-200 p-6 card-hover animate-fade-in-up stagger-${i + 1}`}
                data-testid={`feature-card-${i}`}
              >
                <div className="w-12 h-12 rounded-xl bg-[#FFF0ED] flex items-center justify-center mb-4">
                  {f.icon}
                </div>
                <h3 className="text-lg font-bold text-[#1F2937] font-['Outfit'] mb-2">{f.title}</h3>
                <p className="text-sm text-[#4B5563] leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-20" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#1F2937] rounded-2xl p-8 sm:p-12 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-white font-['Outfit']">
              Bring real-world career experiences to your school.
            </h2>
            <p className="mt-3 text-gray-300 max-w-lg mx-auto">
              Give students opportunities to explore, connect and experience careers before making important choices.
            </p>
            <div className="flex flex-wrap gap-3 justify-center mt-8">
              <Link to="/school-registration">
                <Button className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl font-semibold px-6 py-3 h-auto" data-testid="cta-schools-btn">
                  Register School
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="rounded-xl font-semibold px-6 py-3 h-auto border-white/20 text-white hover:bg-white/10" data-testid="cta-register-btn">
                  Request Demo
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
